/**
 * Persistence.
 *
 * localStorage. Global settings live under fixed keys; everything belonging to
 * a person lives under a key scoped to their profile id, so profiles never see
 * each other's programs, notes or voice history.
 */

const PREFIX = 'pemf_';

export const KEYS = {
  /** Global */
  profiles: `${PREFIX}profiles`,
  activeProfile: `${PREFIX}active_profile`,
  theme: `${PREFIX}theme`,
  onboarded: `${PREFIX}onboarded`,
  device: `${PREFIX}device`,
  seedVersion: `${PREFIX}seed_version`,

  /** Legacy single-profile keys, read once during migration then left alone. */
  legacyPrograms: `${PREFIX}programs`,
  legacyLog: `${PREFIX}log`,
  legacyVoice: `${PREFIX}voice`,
} as const;

/** Per-profile key. */
export function profileKey(profileId: string, what: 'programs' | 'log' | 'voice'): string {
  return `${PREFIX}p_${profileId}_${what}`;
}

export function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function save(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota exceeded or private mode — non-fatal.
  }
}

export function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Export everything for backup. */
export function exportAll(): string {
  const dump: Record<string, unknown> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PREFIX)) dump[key] = load(key, null);
    }
  } catch {
    /* ignore */
  }
  return JSON.stringify({ exportedAt: new Date().toISOString(), data: dump }, null, 2);
}
